import { useQuery } from "@tanstack/react-query";
import { FundingApplicationSchemas } from "@wayflyer/api-types";

import {
  getFundingApplicationStagesQueryOptions,
  getFundingApplicationQueryOptions,
  getStageCompletionStatusQueryOptions,
} from "../../funding-application/queries/funding-application";

import { ONBOARDING_STEP_IDS, ONBOARDING_SUBSTEP_IDS, DOCUMENT_TYPE_TO_SUBSTEP } from "./constants";

export interface OnboardingWorkflowStateOptions {
  customerDomain: string | null;
  countryOfIncorporation?: string;
  hasBusinessCountryData?: boolean;
  hasRevenueData?: boolean;
  hasBusinessLocationData?: boolean;
  hasBusinessDetailsData?: boolean;
}

export interface OnboardingWorkflowState {
  completedStepIds: string[];
  completedSubstepIds: string[];
  isStateRequired: boolean;
  fundingApplication: FundingApplicationSchemas.CustomerFundingApplicationScreen | null | undefined;
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen | undefined;
}

export function useOnboardingWorkflowState(options: OnboardingWorkflowStateOptions): OnboardingWorkflowState {
  const {
    customerDomain,
    countryOfIncorporation,
    hasBusinessCountryData = false,
    hasRevenueData = false,
    hasBusinessLocationData = false,
    hasBusinessDetailsData = false,
  } = options;

  const isStateRequired = countryOfIncorporation === "US" || countryOfIncorporation === "CA";

  const { data: fundingApplication } = useQuery({
    ...getFundingApplicationQueryOptions(customerDomain ?? ""),
    enabled: !!customerDomain,
  });

  const { data: stagesConfig } = useQuery({
    ...getFundingApplicationStagesQueryOptions(customerDomain ?? ""),
    enabled: !!customerDomain,
  });

  const completedStepIds: string[] = [];
  const completedSubstepIds: string[] = [];

  if (hasBusinessCountryData || fundingApplication?.applicationState === "in_progress") {
    completedStepIds.push(ONBOARDING_STEP_IDS.BUSINESS_COUNTRY);
  }
  if (hasRevenueData || fundingApplication?.applicationState === "in_progress") {
    completedStepIds.push(ONBOARDING_STEP_IDS.MONTHLY_REVENUE);
  }
  if ((hasBusinessLocationData || fundingApplication?.applicationState === "in_progress") && isStateRequired) {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION);
  }
  if (hasBusinessDetailsData || fundingApplication?.applicationState === "in_progress") {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION);
  }

  const isBusinessModelCompleted =
    !!fundingApplication?.id &&
    ((fundingApplication.businessModel !== null && fundingApplication.businessModel !== undefined) ||
      (fundingApplication.reportedCustomerSegment && fundingApplication.reportedCustomerSegment.length > 0) ||
      (fundingApplication.reportedSalesChannel && fundingApplication.reportedSalesChannel.length > 0) ||
      (fundingApplication.reportedProductOffering && fundingApplication.reportedProductOffering.length > 0) ||
      fundingApplication.applicationState === "in_progress");

  if (isBusinessModelCompleted) {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL);
  }

  if (checkBusinessDetailsCompletion(completedSubstepIds, isStateRequired)) {
    completedStepIds.push(ONBOARDING_STEP_IDS.BUSINESS_DETAILS);
  }

  const hasFundingApplication = !!fundingApplication?.id;
  const { data: stageCompletionStatus } = useQuery({
    ...getStageCompletionStatusQueryOptions(customerDomain ?? ""),
    enabled: !!isBusinessModelCompleted,
  });

  const expectedConnectionsSubsteps = getExpectedConnectionsSubsteps(stagesConfig, fundingApplication);

  if (hasFundingApplication && stageCompletionStatus && stagesConfig) {
    markConnectionsSubstepsComplete(stagesConfig, stageCompletionStatus, fundingApplication, completedSubstepIds);
  }

  if (checkConnectionsCompletion(expectedConnectionsSubsteps, completedSubstepIds)) {
    completedStepIds.push(ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA);
  }

  return {
    completedStepIds,
    completedSubstepIds,
    isStateRequired,
    fundingApplication,
    stagesConfig,
  };
}

function checkBusinessDetailsCompletion(completedSubstepIds: string[], isStateRequired: boolean): boolean {
  const requiredSubsteps = [
    ...(isStateRequired ? [ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION] : []),
    ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION,
    ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL,
  ];
  return requiredSubsteps.every((substepId) => completedSubstepIds.includes(substepId));
}

// Several document types can share one substep id - group by substep so callers treat it as one unit.
function groupDocumentTypesBySubstep(
  documents: FundingApplicationSchemas.DocumentStageDataScreen[]
): Map<string, FundingApplicationSchemas.DocumentType[]> {
  const documentTypesBySubstepId = new Map<string, FundingApplicationSchemas.DocumentType[]>();
  documents.forEach((doc) => {
    const substepConfig = DOCUMENT_TYPE_TO_SUBSTEP[doc.documentType];
    if (substepConfig) {
      const documentTypes = documentTypesBySubstepId.get(substepConfig.id) ?? [];
      documentTypes.push(doc.documentType);
      documentTypesBySubstepId.set(substepConfig.id, documentTypes);
    }
  });
  return documentTypesBySubstepId;
}

function getExpectedConnectionsSubsteps(
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen | undefined,
  fundingApplication: FundingApplicationSchemas.CustomerFundingApplicationScreen | null | undefined
): string[] {
  const substeps: string[] = [];

  if (!stagesConfig) {
    return substeps;
  }

  if (stagesConfig.banking) {
    substeps.push(ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT);
  }
  if (stagesConfig.includePlatformSelection) {
    substeps.push(ONBOARDING_SUBSTEP_IDS.PLATFORMS);
  }

  if (stagesConfig.accounting?.platformRequired && fundingApplication?.accountingDocumentsRequired) {
    const balanceSheetConfig = DOCUMENT_TYPE_TO_SUBSTEP["balance-sheet"];
    const profitAndLossConfig = DOCUMENT_TYPE_TO_SUBSTEP["profit-and-loss"];
    if (balanceSheetConfig) {
      substeps.push(balanceSheetConfig.id);
    }
    if (profitAndLossConfig) {
      substeps.push(profitAndLossConfig.id);
    }
  }

  if (stagesConfig.documents) {
    groupDocumentTypesBySubstep(stagesConfig.documents).forEach((_documentTypes, substepId) => {
      substeps.push(substepId);
    });
  }
  if (stagesConfig.creditConsent) {
    substeps.push(ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK);
  }

  return substeps;
}

function markConnectionsSubstepsComplete(
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen,
  stageCompletionStatus: FundingApplicationSchemas.StageCompletionStatus,
  fundingApplication: FundingApplicationSchemas.CustomerFundingApplicationScreen | null | undefined,
  completedSubstepIds: string[]
): void {
  if (stagesConfig.banking && (stageCompletionStatus.banking ?? false)) {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT);
  }

  if (
    stagesConfig.includePlatformSelection &&
    (stageCompletionStatus.revenue ?? false) &&
    (stageCompletionStatus.marketing ?? false) &&
    (stageCompletionStatus.accounting ?? false)
  ) {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.PLATFORMS);
  }

  if (stagesConfig.accounting?.platformRequired && fundingApplication?.accountingDocumentsRequired) {
    const balanceSheetConfig = DOCUMENT_TYPE_TO_SUBSTEP["balance-sheet"];
    const profitAndLossConfig = DOCUMENT_TYPE_TO_SUBSTEP["profit-and-loss"];

    if (balanceSheetConfig && profitAndLossConfig && (stageCompletionStatus.accounting ?? false)) {
      completedSubstepIds.push(balanceSheetConfig.id);
      completedSubstepIds.push(profitAndLossConfig.id);
    }
  }

  if (stagesConfig.documents && stagesConfig.documents.length > 0) {
    // A substep is complete only once every document type mapped to it is complete.
    groupDocumentTypesBySubstep(stagesConfig.documents).forEach((documentTypes, substepId) => {
      const allComplete = documentTypes.every(
        (documentType) => stageCompletionStatus.documents?.[documentType] ?? false
      );
      if (allComplete) {
        completedSubstepIds.push(substepId);
      }
    });
  }

  if (stagesConfig.creditConsent && (stageCompletionStatus.creditConsent ?? false)) {
    completedSubstepIds.push(ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK);
  }
}

function checkConnectionsCompletion(expectedSubsteps: string[], completedSubstepIds: string[]): boolean {
  return expectedSubsteps.length > 0 && expectedSubsteps.every((substepId) => completedSubstepIds.includes(substepId));
}
